/**
 * useStudents — React Query 기반 students 단순캐시 (옵션2 PoC, ISSUE_167)
 *
 * 서버(Supabase) = SSOT. React Query = 클라 단일 캐시 권위. 낙관은 onMutate 한 곳에만.
 * Zustand `useStudentsStore` 는 이 쿼리의 **읽기전용 미러** — SessionBuilder·세션 서비스 등
 * 비-React `getState().students` 소비처 호환을 위해 유지(데이터 소유권은 React Query).
 *
 * 캐시 형태 = StudentFromDb[] (JSON-safe → persist 직렬화 안전). `select` 로 Student[] 투영.
 * 낙관 패치도 StudentFromDb 형태로 일원화 → 이중 매핑 없음.
 *
 * 단순캐시 모델: pendingMutations 큐·MasterDataSyncProcessor·readThroughCache 머지를 걷어냄.
 *
 * @module core/query/useStudents
 */

import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { queryKeys, queryClient as globalQueryClient } from "~/shared/lib/query-client";
import { useAuthStore } from "~/core/store/useAuthStore";
import { useWorkspaceStore } from "~/core/store/useWorkspaceStore";
import { useStudentsStore, type Student } from "~/core/store/students/useStudentsStore";
import {
  fetchStudentsForWorkspace,
  createStudentRemote,
  updateStudentRemote,
  applyStudentPatch,
  type StudentFromDb,
  type CreateStudentInput,
  type StudentPatch,
} from "~/services/students/studentsService";
import type { WorkspaceContext } from "~/services/sessions/sessionsService";

// ============================================================================
// GRADE 매핑 (useFetchStudents 에서 이전 — DB grade 문자열 ↔ store 숫자)
// ============================================================================

/** "중3" -> 9, "고1" -> 10, "초6" -> 6, "N수" -> 13, "일반" -> 18 */
export function parseGrade(gradeStr: string | undefined): number | undefined {
  if (!gradeStr) return undefined;
  if (gradeStr === "N수") return 13;
  if (gradeStr === "일반") return 18;

  const match = gradeStr.match(/^(초|중|고|대)(\d)$/);
  if (!match || !match[1] || !match[2]) return undefined;

  const level = match[1];
  const gradeNum = parseInt(match[2], 10);

  switch (level) {
    case "초":
      return gradeNum; // 1-6
    case "중":
      return 6 + gradeNum; // 7-9
    case "고":
      return 9 + gradeNum; // 10-12
    case "대":
      return 13 + gradeNum; // 14-17
    default:
      return undefined;
  }
}

/** StudentFromDb → store Student (conditional spread for exactOptionalPropertyTypes) */
export function toStoreStudent(dbStudent: StudentFromDb): Student {
  const grade = parseGrade(dbStudent.grade);

  return {
    id: dbStudent.id,
    name: dbStudent.name,
    status: dbStudent.status ?? ("active" as const),
    createdAt: dbStudent.enrolledAt ? new Date(dbStudent.enrolledAt) : new Date(),
    updatedAt: new Date(),
    ...(dbStudent.school && { school: dbStudent.school }),
    ...(dbStudent.schoolId && { schoolId: dbStudent.schoolId }),
    ...(grade !== undefined && { grade }),
    ...(dbStudent.phone && { phone: dbStudent.phone }),
    ...(dbStudent.guardianPhone && { parentPhone: dbStudent.guardianPhone }),
    // ISSUE_167: 특이사항은 DB `memo` 컬럼에 영속화되나 UI 컴포넌트(StudentModal·SlideOver·
    // InfoTabContent 등)는 `remarks`를 읽음 → memo를 remarks로도 매핑(마이그레이션 회귀 수정).
    ...(dbStudent.memo && { memo: dbStudent.memo, remarks: dbStudent.memo }),
  };
}

/** 서버에서 온 raw 목록 → UI Student[] (soft-deleted [삭제됨] 제외, 단일 필터 지점) */
export function selectStudents(rows: StudentFromDb[]): Student[] {
  return rows.filter((s) => !s.name.startsWith("[삭제됨]")).map(toStoreStudent);
}

// ============================================================================
// WORKSPACE 컨텍스트 헬퍼
// ============================================================================

/** workspace → 캐시 키 분절 (academyId) */
export function workspaceKeyOf(workspace: WorkspaceContext): string {
  return workspace.academyId!;
}

/** students 목록 queryKey */
export function studentsListKey(workspaceKey: string) {
  return queryKeys.students.list(workspaceKey);
}

/**
 * 현재 워크스페이스 컨텍스트 (로딩 중·미인증이면 null).
 * 매 렌더 새 객체 → queryKey 에 직접 쓰지 말고 workspaceKeyOf 로 문자열화해 사용.
 */
export function useWorkspaceContext(): WorkspaceContext | null {
  const profileId = useAuthStore((s) => s.profile?.id);
  const active = useWorkspaceStore((s) => s.active);
  const isWorkspaceLoading = useWorkspaceStore((s) => s.isLoading);

  // ISSUE_182: 학원 미선택(active.id==='', 예: /spaces 런처)이면 워크스페이스 없음 → 쿼리 비활성
  if (isWorkspaceLoading || !profileId || !active.id) return null;
  return { type: "academy", academyId: active.id };
}

// ============================================================================
// READ — useStudentsQuery
// ============================================================================

/**
 * 현재 워크스페이스 학생 목록.
 * - 캐시: StudentFromDb[] (persist 영속화 대상). select 로 Student[] 투영.
 * - ISSUE_167: 서버 SSOT — fetch는 syncEnabled 무관 항상(미결제도 서버에서 읽음).
 *   realtime(멀티기기 라이브)만 useRealtimeStudents에서 게이트. 프리미엄 모델은 후속 이슈.
 */
export function useStudentsQuery() {
  const workspace = useWorkspaceContext();
  const workspaceKey = workspace ? workspaceKeyOf(workspace) : "none";

  return useQuery<StudentFromDb[], Error, Student[]>({
    queryKey: studentsListKey(workspaceKey),
    enabled: !!workspace,
    queryFn: () => fetchStudentsForWorkspace(workspace!),
    select: selectStudents,
  });
}

/**
 * 미러 투영 — React Query 데이터를 Zustand 스토어로 밀어넣는 **단 하나의 통로**.
 * _index.tsx 최상위에서 1회 마운트. 워크스페이스 전환 시 queryKey 변경 → 즉시 refetch → 미러 갱신.
 */
export function useStudentsMirror(): { isLoading: boolean; error: string | null } {
  const query = useStudentsQuery();
  const setStudents = useStudentsStore((s) => s.setStudents);
  const data = query.data;
  const loading = query.isPending && query.fetchStatus !== "idle";

  // 워크스페이스 전환 시 새 키 data 가 pending(undefined)이면 스토어를 즉시 비운다
  // (이전 학원 학생 잔존=크로스테넌트 방지). isLoading 도 함께 밀어넣어야, 비우는
  // 순간 store.isLoading=false 로 StudentManageModule 의 EmptyState 가 튀지 않는다.
  useEffect(() => {
    setStudents(data ?? [], loading);
  }, [data, loading, setStudents]);

  return {
    isLoading: loading,
    error: query.error ? query.error.message : null,
  };
}

// ============================================================================
// WRITE — 그래뉼러 RQ mutation (낙관 onMutate + 롤백 onError + invalidate onSettled)
// ============================================================================

type StudentsCache = StudentFromDb[];

/** 낙관 setQueryData + 스냅샷 헬퍼 (공통 onMutate 골격) */
async function optimistic(
  qc: QueryClient,
  workspace: WorkspaceContext,
  updater: (rows: StudentsCache) => StudentsCache,
): Promise<{ key: ReturnType<typeof studentsListKey>; prev: StudentsCache | undefined }> {
  const key = studentsListKey(workspaceKeyOf(workspace));
  await qc.cancelQueries({ queryKey: key });
  const prev = qc.getQueryData<StudentsCache>(key);
  qc.setQueryData<StudentsCache>(key, (old) => updater(old ?? []));
  return { key, prev };
}

interface CreateVars {
  id: string;
  input: CreateStudentInput;
}

/** 학생 생성 — 낙관 append + (sync ON 시) Supabase upsert */
export function useCreateStudentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: CreateVars) => {
      await createStudentRemote({ ...input, id });
    },
    onMutate: ({ id, input }: CreateVars) => {
      const row: StudentFromDb = { id, name: input.name, status: input.status ?? "active" };
      if (input.phone) row.phone = input.phone;
      if (input.email) row.email = input.email;
      if (input.school) row.school = input.school;
      if (input.schoolId) row.schoolId = input.schoolId;
      if (input.grade) row.grade = input.grade;
      if (input.guardianName) row.guardianName = input.guardianName;
      if (input.guardianPhone) row.guardianPhone = input.guardianPhone;
      if (input.memo) row.memo = input.memo;
      if (input.enrolledAt) row.enrolledAt = input.enrolledAt;
      if (input.withdrawnAt) row.withdrawnAt = input.withdrawnAt;
      return optimistic(qc, input.workspace, (rows) => [...rows, row]);
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(ctx.key, ctx.prev);
    },
    onSettled: (_d, _e, _v, ctx) => {
      if (ctx) qc.invalidateQueries({ queryKey: ctx.key });
    },
  });
}

interface UpdateVars {
  id: string;
  workspace: WorkspaceContext;
  /** StudentPatch — 낙관 머지 + Supabase update 양쪽에 동일. null = 컬럼 클리어(예: 복원 시 withdrawnAt) */
  patch: StudentPatch;
}

/** 학생 수정 (퇴원/복원/상태변경/특이사항 포함 — 전부 patch 로 표현) */
export function useUpdateStudentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, workspace, patch }: UpdateVars) => {
      await updateStudentRemote(id, patch, workspace);
    },
    onMutate: ({ id, workspace, patch }: UpdateVars) =>
      optimistic(qc, workspace, (rows) =>
        rows.map((r) => (r.id === id ? applyStudentPatch(r, patch) : r)),
      ),
    onError: (_e, _v, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(ctx.key, ctx.prev);
    },
    onSettled: (_d, _e, _v, ctx) => {
      if (ctx) qc.invalidateQueries({ queryKey: ctx.key });
    },
  });
}

interface ForceDeleteVars {
  id: string;
  name: string;
  workspace: WorkspaceContext;
}

/**
 * 완전 삭제 (soft-delete: 이름 [삭제됨] 리네임 + 개인정보 클리어).
 * 낙관적으로 목록에서 제거(select 가 [삭제됨] 필터하므로 캐시에서도 제거).
 */
export function useForceDeleteStudentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name, workspace }: ForceDeleteVars) => {
      await updateStudentRemote(
        id,
        { name: `[삭제됨] ${name}`, phone: null, email: null, guardianPhone: null, memo: null },
        workspace,
      );
    },
    onMutate: ({ id, workspace }: ForceDeleteVars) =>
      optimistic(qc, workspace, (rows) => rows.filter((r) => r.id !== id)),
    onError: (_e, _v, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(ctx.key, ctx.prev);
    },
    onSettled: (_d, _e, _v, ctx) => {
      if (ctx) qc.invalidateQueries({ queryKey: ctx.key });
    },
  });
}

// ============================================================================
// REALTIME 헬퍼 — 다른 기기 변경 시 캐시 무효화 (비-React, RealtimeManager 콜백용)
// ============================================================================

/** 특정 워크스페이스 students 캐시 무효화 → 다음 refetch 로 서버 진실원본 재확인 */
export function invalidateStudents(workspaceKey: string): void {
  void globalQueryClient.invalidateQueries({ queryKey: studentsListKey(workspaceKey) });
}
